import React__default, { useLayoutEffect, useEffect, useRef, useState } from 'react';
import { E as ErrorBoundary, c as classnames, H as HighchartsReact } from './vendor-bd529190.js';
import { createUseStyles, useTheme } from 'react-jss';
import { defineMessages, t, T } from '@sm/intl';
import { Box } from '@wds/box';
import { Card } from '@wds/card';
import { Typography } from '@wds/typography';
import { IconFilter, IconWarning, IconArrowLeft, IconArrowRight } from '@wds/icons';
import Highcharts, { numberFormat } from 'highcharts';
import 'react-dom/server';
import './theme-4445522f.js';
import { withSpacing } from '@wds/utils';
import { Button } from '@wds/button';
import '@wds/tooltip';
import assert from 'assert';
import merge from 'lodash.merge';
import HighchartsAccessibility from 'highcharts/modules/accessibility';
import '@wds/styles';
const useStyles$f = createUseStyles(theme => ({
  wdsIcons: ({
    color,
    size
  }) => {
    var _a, _b, _c;

    const fontSize = size ? {
      fontSize: theme.iconSize[size]
    } : {
      fontSize: 'inherit'
    };
    const chosenColor = // @ts-expect-error Fallback styles are acceptable
    ((_a = theme.palette) === null || _a === void 0 ? void 0 : _a.text[color]) || ((_c = (_b = theme.palette) === null || _b === void 0 ? void 0 : _b[color]) === null || _c === void 0 ? void 0 : _c.main) || 'currentColor';
    return { ...fontSize,
      width: '1em',
      height: '1em',
      display: 'inline-block',
      verticalAlign: 'middle',
      textAlign: 'center',
      fill: chosenColor
    };
  }
})); // https://reactjs.org/docs/higher-order-components.html#convention-wrap-the-display-name-for-easy-debugging

const getDisplayName = Component => Component.displayName || Component.name || 'Component';

const withIcon = Component => {
  const WithIcon = ({
    color,
    title,
    size,
    ...rest
  }) => {
    const classes = useStyles$f({
      color,
      size
    });
    return React__default.createElement("svg", {
      className: classes.wdsIcons,
      viewBox: "0 0 16 16",
      preserveAspectRatio: "xMidYMid",
      "aria-label": title,
      role: title ? 'img' : 'presentation',
      ...rest
    }, React__default.createElement(Component, null));
  };

  WithIcon.displayName = `WithIcon(${getDisplayName(Component)})`;

  WithIcon.getOriginalComponent = () => Component;

  return WithIcon;
};

const DocumentXIconPath = () => React__default.createElement("path", {
  d: "m5.94.005 8.012.012C14.53.018 15 .487 15 1.067V14.95c0 .58-.47 1.05-1.05 1.05H2.05C1.47 16 1 15.53 1 14.95V4.94L5.94.004zM7 1.505V4.95c0 .541-.41.987-.936 1.044L5.95 6H2.5v8.5h11V1.514L7 1.505zM6.557 7.53 8 8.973 9.443 7.53a.765.765 0 0 1 1.083 1.082l-1.444 1.443 1.444 1.444a.765.765 0 0 1-1.083 1.082L8 11.138 6.557 12.58A.765.765 0 1 1 5.474 11.5l1.444-1.444-1.444-1.443A.765.765 0 1 1 6.557 7.53zM5.5 2.563 3.561 4.5H5.5V2.562z"
});

var IconDocumentX = withIcon(DocumentXIconPath);
const useStyles$e = createUseStyles(theme => {
  var _a, _b;

  return {
    fallbackComponent: {
      fontFamily: (_b = (_a = theme.questionBody) === null || _a === void 0 ? void 0 : _a.fontFamily) !== null && _b !== void 0 ? _b : {},
      color: theme.questionColor,
      border: `1px solid ${theme.questionColor}`,
      borderRadius: '5px',
      width: '100%',
      height: '100%',
      minHeight: '220px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '& svg': {
        fill: theme.questionColor
      }
    },
    errorMessage: {
      marginLeft: `8px` // equivalent to wrench theme.spacing[2]

    }
  };
});
const COPY$7 = defineMessages({
  ERROR: {
    id: 'FallbackComponent.ERROR',
    defaultMessage: 'Error loading question',
    description: '[Type: label][Vis: high] - error message indicating a question could not be loaded'
  }
});

const FallbackComponent = () => {
  const {
    fallbackComponent,
    errorMessage
  } = useStyles$e();
  return React__default.createElement("div", {
    className: fallbackComponent
  }, React__default.createElement(IconDocumentX, null), React__default.createElement("p", {
    className: errorMessage
  }, t(COPY$7.ERROR)));
};

const withErrorBoundary = Component => {
  return props => React__default.createElement(ErrorBoundary, {
    FallbackComponent: FallbackComponent
  }, React__default.createElement(Component, { ...props
  }));
};

var _a$2;
/**
 * SSR Safe version of useLayoutEffect
 * @see https://code.corp.surveymonkey.com/webplatform/smweb/blob/master/apps/contentweb/src/app/pages/MRX/helpers/useIsomorphicLayoutEffect.js
 *
 * Note: `NOSONAR` is used to bypass a `minor:code-smell` error that states `createElement` is deprecated.
 * The eslint rule above disables the spaced comments to use NOSONAR as stated in the docs.
 */


const useSSRSafeLayoutEffect = typeof window !== 'undefined' && typeof ((_a$2 = window === null || window === void 0 ? void 0 : window.document) === null || _a$2 === void 0 ? void 0 : _a$2.createElement) !== 'undefined' //NOSONAR
? useLayoutEffect : useEffect;

function useDimensions() {
  const target = useRef(null);
  const [dimensions, setDimensions] = useState({});
  useSSRSafeLayoutEffect(() => {
    if (target.current) {
      const rect = {
        width: target.current.offsetWidth,
        height: target.current.offsetHeight
      };
      setDimensions(rect);
    }
  }, []);
  return [target, dimensions];
}

const useStyles$d = createUseStyles({
  truncateText: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%'
  }
}); // Internal component only

const Truncate = ({
  children
}) => {
  const {
    truncateText
  } = useStyles$d();
  return React__default.createElement("span", {
    className: truncateText
  }, children);
};

const COPY$6 = defineMessages({
  ANSWERED: {
    id: 'QuestionCardTitle.ANSWERED',
    defaultMessage: 'Answered: {answeredCount}',
    description: '[Type: label][Vis: med] - label to show how many questions are answered'
  },
  SELECTED: {
    id: 'QuestionCardTitle.SELECTED',
    defaultMessage: 'Selected: {selectedCount}',
    description: '[Type: label][Vis: med] - label to show how many questions are selected'
  },
  SKIPPED: {
    id: 'QuestionCardTitle.SKIPPED',
    defaultMessage: 'Skipped: {skippedCount}',
    description: '[Type: label][Vis: med] - label to show how many questions are skipped'
  }
});

const QuestionCardTitle = ({
  title,
  answeredCount,
  skippedCount,
  selectedCount,
  headingTag = 'h2'
}) => React__default.createElement("div", null, React__default.createElement(Box, {
  mb: 2,
  mr: 2
}, React__default.createElement(Typography, {
  title: title,
  component: headingTag,
  variant: "cardTitle",
  translate: "no"
}, React__default.createElement(Truncate, null, title))), React__default.createElement(Box, {
  element: "ul",
  display: "flex",
  flexDirection: "row"
}, Number.isInteger(answeredCount) && React__default.createElement("li", null, React__default.createElement(Box, {
  mr: 4,
  element: "span"
}, React__default.createElement(Typography, {
  component: "span",
  variant: "bodySm",
  color: "darkMuted"
}, React__default.createElement(T, {
  desc: COPY$6.ANSWERED,
  values: {
    answeredCount
  }
})))), Number.isInteger(skippedCount) && React__default.createElement("li", null, React__default.createElement(Box, {
  mr: 4,
  element: "span"
}, React__default.createElement(Typography, {
  component: "span",
  variant: "bodySm",
  color: "darkMuted"
}, React__default.createElement(T, {
  desc: COPY$6.SKIPPED,
  values: {
    skippedCount
  }
})))), Number.isInteger(selectedCount) && React__default.createElement("li", null, React__default.createElement(Typography, {
  component: "span",
  variant: "bodySm",
  color: "darkMuted"
}, React__default.createElement(T, {
  desc: COPY$6.SELECTED,
  values: {
    selectedCount
  }
})))));

const useStyles$c = createUseStyles(theme => ({
  container: ({
    isEditable,
    width,
    height
  }) => {
    const hover = isEditable && {
      '& [class*="wdsCard-"]:hover': {
        cursor: 'all-scroll'
      }
    };
    return {
      hover,
      width: width || '100%',
      height: height || '100%',
      minWidth: '300px',
      minHeight: '250px',
      // Workaround: Card doesn't allow `className`.
      '& > div': {
        height: '100%'
      }
    };
  },
  keyline: {
    borderTop: [theme.keyline.borderWidth, theme.keyline.borderStyle, theme.keyline.borderColor]
  },
  cardWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%'
  },
  contentWrapper: {
    flex: '1 1 0%',
    overflow: 'hidden',
    position: 'relative'
  },
  addonWrapper: {
    position: 'absolute',
    top: 0,
    right: 0
  },
  flexReset: {
    minWidth: 0
  }
}));
const COPY$5 = defineMessages({
  FILTER_APPLIED: {
    id: 'QuestionCardItemFooter.FILTER_APPLIED',
    defaultMessage: 'Rules applied: {filterCount, number}',
    description: '[Type: button][Vis: med] - next button of slider'
  },
  FILTER_LABEL: {
    id: 'QuestionCardItemFooter.FILTER_LABEL',
    defaultMessage: 'Filter',
    description: '[Type: button][Vis: med] - previous button of slider'
  }
});

const QuestionCardItemFooter = ({
  surveyName,
  filterCount = 0
}) => {
  const {
    keyline,
    flexReset
  } = useStyles$c({});
  return React__default.createElement("div", {
    className: keyline
  }, React__default.createElement(Box, {
    display: "flex",
    flexJustify: "space-between",
    flexAlign: "baseline",
    pt: 2,
    flexWrap: false
  }, React__default.createElement("div", {
    className: flexReset,
    translate: "no"
  }, React__default.createElement(Typography, {
    component: "h5",
    variant: "bodySm",
    color: "secondary",
    title: surveyName
  }, React__default.createElement("strong", null, React__default.createElement(Truncate, null, surveyName)))), React__default.createElement(Box, {
    display: "flex",
    flexAlign: "center",
    title: t(COPY$5.FILTER_APPLIED, {
      filterCount
    })
  }, React__default.createElement(Box, {
    element: "span",
    mr: 1
  }, React__default.createElement(IconFilter, {
    title: t(COPY$5.FILTER_LABEL),
    color: "secondary",
    size: "md"
  })), React__default.createElement(Typography, {
    component: "span",
    variant: "bodySm",
    color: "darkMuted"
  }, React__default.createElement("strong", null, "(", filterCount, ")")))));
};

const DashboardCardLayoutTemplate = ({
  addon,
  answeredCount,
  filterCount,
  selectedCount,
  skippedCount,
  surveyName,
  children,
  title,
  isEditable = false,
  width,
  height
}) => {
  const [root, {
    height: rHeight,
    width: rWidth
  }] = useDimensions();
  const [header, {
    height: hHeight
  }] = useDimensions();
  const [footer, {
    height: fHeight
  }] = useDimensions();
  const [contentSize, setContentSize] = React__default.useState({
    contentWidth: 0,
    contentHeight: 0
  });
  useSSRSafeLayoutEffect(() => {
    if (!(Number.isNaN(rHeight) || Number.isNaN(hHeight) || Number.isNaN(fHeight))) {
      setContentSize({
        contentHeight: rHeight - (hHeight + fHeight),
        contentWidth: rWidth
      });
    }
  }, [rHeight, hHeight, fHeight, rWidth]);
  const {
    cardWrapper,
    contentWrapper,
    addonWrapper,
    container
  } = useStyles$c({
    isEditable,
    width,
    height
  }); // Allow for children to be rendered with content-dimensions
  // or rendered as is

  const content = typeof children === 'function' ? children(contentSize) : children;
  return React__default.createElement("div", {
    ref: root,
    className: container
  }, React__default.createElement(Card, {
    padding: false,
    interactive: true
  }, React__default.createElement("div", {
    className: cardWrapper
  }, addon && React__default.createElement("div", {
    className: addonWrapper
  }, addon), React__default.createElement("div", {
    ref: header
  }, React__default.createElement(Box, {
    pl: 4,
    pr: 7,
    pt: 4,
    mb: 1
  }, React__default.createElement(QuestionCardTitle, {
    title: title,
    answeredCount: answeredCount,
    skippedCount: skippedCount,
    selectedCount: selectedCount
  }))), React__default.createElement("div", {
    className: contentWrapper
  }, content), React__default.createElement("div", {
    ref: footer
  }, React__default.createElement(Box, {
    px: 3,
    pb: 3
  }, React__default.createElement(QuestionCardItemFooter, {
    surveyName: surveyName,
    filterCount: filterCount
  }))))));
};

const initialSentiments = {
  negative: 0,
  neutral: 0,
  positive: 0,
  unknown: 0
};

const sumResponsesBySentiment = (sentiments, response) => {
  if (!response.sentiment) {
    return sentiments;
  }

  return { ...sentiments,
    [response.sentiment]: (sentiments[response.sentiment] || 0) + 1
  };
};

const convertTextResponsesToSentiments = responses => responses.reduce(sumResponsesBySentiment, initialSentiments);

const useStyles$b = createUseStyles({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%'
  }
});
const COPY$4 = {
  ICON_LABEL: 'No matching responses.'
};

const ResponseError = () => {
  const {
    container
  } = useStyles$b();
  return React__default.createElement("div", {
    className: container
  }, React__default.createElement(Typography, {
    align: "center",
    variant: "bodySm"
  }, React__default.createElement(IconWarning, {
    size: "sm",
    role: "presentation",
    title: ""
  }), " ", COPY$4.ICON_LABEL));
};

const sentimentColors = {
  unknown: '#9DA5AA',
  neutral: '#F9BE00',
  negative: '#DB4D5C',
  positive: '#00BF6F'
};

const Negative = () => React__default.createElement("path", {
  fill: sentimentColors.negative,
  d: "M16.875 10.125a1.874 1.874 0 11-.001-3.749 1.874 1.874 0 01.001 3.749zM6 18.563a6 6 0 0112 0H6zM5.25 8.25a1.874 1.874 0 113.749-.001 1.874 1.874 0 01-3.749.001zM12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0z",
  fillRule: "evenodd"
});

const NegativeIcon = withIcon(Negative);

const Neutral = () => React__default.createElement("path", {
  d: "M4.875 17.813h14.25v-3H4.875v3zm2.25-11.438a1.874 1.874 0 11.001 3.749 1.874 1.874 0 01-.001-3.749zm9.75 0a1.874 1.874 0 11.001 3.749 1.874 1.874 0 01-.001-3.749zM12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0z",
  fill: sentimentColors.neutral,
  fillRule: "evenodd"
});

const NeutralIcon = withIcon(Neutral);

const Positive = () => React__default.createElement("path", {
  fill: sentimentColors.positive,
  d: "M16.875 10.125a1.874 1.874 0 11-.001-3.749 1.874 1.874 0 01.001 3.749M12 19.313a6 6 0 01-6-6h12a6 6 0 01-6 6M5.25 8.25a1.874 1.874 0 113.749-.001 1.874 1.874 0 01-3.749.001M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0",
  fillRule: "evenodd"
});

const PositiveIcon = withIcon(Positive);

const Unknown = () => React__default.createElement("path", {
  d: "M14.177 11.626c-1.011.874-1.408 1.425-1.427 2.374h-2.42c0-1.5.575-2.202 1.468-3 .773-.702 1.625-1.158 1.625-2.26 0-.892-.634-1.65-1.844-1.65-1.229 0-2.041.872-2.22 2.201L7 8.645C7.338 6.746 8.963 5 11.739 5 14.414 5 16 6.557 16 8.55c0 1.444-.872 2.24-1.823 3.076M11.5 19c-.89 0-1.5-.669-1.5-1.51S10.61 16 11.5 16s1.5.649 1.5 1.49S12.39 19 11.5 19M12 0C5.373 0 0 5.373 0 12c0 6.628 5.373 12 12 12 6.628 0 12-5.372 12-12 0-6.627-5.372-12-12-12",
  fill: sentimentColors.unknown,
  fillRule: "evenodd"
});

const UnknownIcon = withIcon(Unknown);
var Icons = {
  negative: NegativeIcon,
  neutral: NeutralIcon,
  positive: PositiveIcon,
  unknown: UnknownIcon
};

const SentimentIcon = ({
  sentiment,
  size,
  title,
  ...props
}) => {
  var _a;

  const Icon = (_a = Icons[sentiment]) !== null && _a !== void 0 ? _a : Icons.unknown;
  return React__default.createElement(Icon, {
    size: size,
    title: title,
    // Sentiment icons are 24px square, Wrench defaults to 16px
    viewBox: "0 0 24 24",
    // Color will be overwritten by each Icon component
    color: "primary",
    ...props
  });
};

const sum = values => values.reduce((total, current) => total + current, 0);

const calculatePercentage = (count, total) => count && total ? count / total * 100 : 0;

const SENTIMENTS_ORDERED = ['positive', 'neutral', 'negative', 'unknown'];

const useSentimentChartItems = ({
  negative,
  neutral,
  positive,
  unknown
}) => React__default.useMemo(() => {
  const total = sum([negative, neutral, positive, unknown]);
  const sentiments = {
    negative,
    neutral,
    positive,
    unknown
  }; // Sort sentiments for display, calculate percentage

  const chartItems = SENTIMENTS_ORDERED.map(sentiment => ({
    sentiment,
    count: sentiments[sentiment],
    percentage: calculatePercentage(sentiments[sentiment], total)
  })); // Remove any sentiments with no responses

  return chartItems.filter(({
    count
  }) => count > 0);
}, [negative, neutral, positive, unknown]);

const COPY$3 = defineMessages({
  PERCENT: {
    id: 'sentiment.PERCENT',
    defaultMessage: `{percentage, number, percent}`,
    description: '[Type: label][Vis: med] - percentage rendering'
  },
  SENTIMENT: {
    id: 'sentiment.SENTIMENT',
    defaultMessage: `{sentiment, select, 
      positive {Positive}
      negative {Negative}
      neutral {Neutral}
      other {Unknown}
    }`,
    description: '[Type: label][Vis: med] - sentiment labels'
  },
  SENTIMENT_WITH_PERCENT: {
    id: 'sentiment.SENTIMENT_WITH_PERCENT',
    defaultMessage: `{sentiment, select, 
      positive {Positive: {percentage, number, percent}}
      negative {Negative: {percentage, number, percent}}
      neutral {Neutral: {percentage, number, percent}}
      other {Unknown: {percentage, number, percent}}
    }`,
    description: '[Type: label][Vis: med] - sentiment labels with percentage'
  },
  // note this copy uses UNDETECTED instead of UNKNOWN because it is specific for analyze
  FILTER_BY_SENTIMENT: {
    id: 'sentiment.FILTER_BY_SENTIMENT',
    defaultMessage: `{sentiment, select, 
      positive {Filter by positive sentiment}
      negative {Filter by negative sentiment}
      neutral {Filter by neutral sentiment}
      other {Filter by undetected sentiment}
    }`,
    description: '[Type: title][Vis: med] - filter by sentiment'
  }
});
const useStyles$a = createUseStyles(theme => ({
  chartItem: ({
    width
  }) => ({
    width: width && `${width}%`
  }),
  chartBar: ({
    color
  }) => ({
    border: `1px solid ${theme.palette.background.light}`,
    backgroundColor: color,
    height: '30px'
  })
}));
/* Chart segment layout */

const ChartSegment = ({
  color,
  icon,
  label,
  title,
  width
}) => {
  const {
    chartBar,
    chartItem
  } = useStyles$a({
    color,
    width
  });
  return React__default.createElement("div", {
    "aria-label": label,
    className: chartItem
  }, React__default.createElement(Box, {
    display: "flex",
    flexJustify: "center",
    mb: 3,
    mt: 3
  }, icon), React__default.createElement(Box, {
    mb: 3
  }, React__default.createElement("div", {
    className: chartBar
  })), React__default.createElement(Box, {
    mb: 5
  }, React__default.createElement(Typography, {
    align: "center",
    variant: "bodySm"
  }, title)));
};
/* Sentiment-analysis interface */


const SentimentBarChartSegment = ({
  percentage: percentage100Base,
  sentiment,
  size = 'md'
}) => {
  /** Copy's ICU formatting is 1 based (`1% === 0.01`) */
  const percentage = percentage100Base / 100;
  return React__default.createElement(ChartSegment, {
    color: sentimentColors[sentiment],
    icon: React__default.createElement(SentimentIcon, {
      sentiment: sentiment,
      size: size,
      title: t(COPY$3.SENTIMENT, {
        sentiment
      })
    }),
    label: t(COPY$3.SENTIMENT_WITH_PERCENT, {
      sentiment,
      percentage
    }),
    title: t(COPY$3.PERCENT, {
      percentage
    }),
    width: percentage100Base
  });
};

const SentimentBarChart = ({
  negative = 0,
  neutral = 0,
  positive = 0,
  unknown = 0,
  size
}) => {
  const chartItems = useSentimentChartItems({
    negative,
    neutral,
    positive,
    unknown
  });
  return React__default.createElement(Box, {
    display: "flex"
  }, chartItems.map(({
    sentiment,
    percentage
  }) => React__default.createElement(SentimentBarChartSegment, {
    key: sentiment,
    sentiment: sentiment,
    size: size,
    percentage: percentage
  })));
};

var _a$1, _b$1, _c, _d; // support additional SVG attributes


(_a$1 = Highcharts.AST) === null || _a$1 === void 0 ? void 0 : _a$1.allowedAttributes.push('viewBox');
(_b$1 = Highcharts.AST) === null || _b$1 === void 0 ? void 0 : _b$1.allowedAttributes.push('preserveAspectRatio');
(_c = Highcharts.AST) === null || _c === void 0 ? void 0 : _c.allowedAttributes.push('fill-rule'); // Highcharts 9 removes some HTML tags and attributes: https://www.highcharts.com/blog/changelog/#highcharts-v9.0.0

(_d = Highcharts.AST) === null || _d === void 0 ? void 0 : _d.allowedAttributes.push('data-testid'); // eslint-disable-next-line @typescript-eslint/no-unused-vars

const useStyles$9 = createUseStyles(theme => ({
  scrollY: ({
    height = 'auto',
    maxHeight = 'none',
    scrollable,
    flex
  }) => ({
    display: flex ? 'flex' : undefined,
    flexGrow: flex ? 1 : undefined,
    height: !Number.isNaN(height) && typeof height === 'number' ? `${height}px` : height,
    maxHeight: !Number.isNaN(maxHeight) && typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
    overflowY: scrollable ? 'auto' : 'hidden',
    '&::-webkit-scrollbar-thumb': {
      // This color is brand color 'stone' which is currently not in WrenchTheme
      backgroundColor: '#9DA5AA',
      borderRadius: '6px'
    },
    '&::-webkit-scrollbar': {
      backgroundColor: 'transparent',
      width: '6px',
      height: '6px'
    }
  })
}));

const ScrollableBox = ({
  children,
  height,
  maxHeight,
  scrollable,
  tabIndex,
  flex,
  ...props
}) => {
  const {
    scrollY
  } = useStyles$9({
    height,
    maxHeight,
    scrollable,
    flex
  });
  const {
    className,
    spacingClass,
    ...rest
  } = props;

  const scrollableBoxTabIndex = () => {
    if (tabIndex) {
      return tabIndex;
    }

    return height ? 0 : undefined;
  };

  return React__default.createElement("div", {
    // Scrollable elements should be accessible by keyboard users
    tabIndex: scrollableBoxTabIndex(),
    className: classnames(className, spacingClass, scrollY),
    ...rest
  }, children);
};

var ScrollableBox$1 = withSpacing(ScrollableBox);
const useStyles$8 = createUseStyles(theme => ({
  list: {
    '& li': {
      borderBottom: [theme.keyline.borderWidth, theme.keyline.borderStyle, theme.keyline.borderColor]
    },
    '& li:last-child': {
      borderBottom: 'none'
    }
  },
  listItem: {
    paddingBottom: theme.spacing[3],
    paddingTop: theme.spacing[3]
  },
  textContainer: {
    wordBreak: 'break-word'
  }
}));

const ResponseList = ({
  children
}) => {
  const {
    list
  } = useStyles$8();
  return React__default.createElement("ul", {
    className: list
  }, children);
};

const ResponseListItem = ({
  children,
  icon
}) => {
  const {
    listItem,
    textContainer
  } = useStyles$8();
  return React__default.createElement("li", {
    className: listItem
  }, React__default.createElement(Box, {
    element: "span",
    display: "flex",
    flexDirection: "row",
    flexAlign: "baseline"
  }, icon && React__default.createElement(Box, {
    element: "span",
    mr: 2
  }, icon), React__default.createElement(Typography, {
    component: "p",
    variant: "bodySm"
  }, React__default.createElement("span", {
    className: textContainer
  }, children))));
};

const useStyles$7 = createUseStyles({
  container: {
    margin: 'auto',
    height: '100%'
  }
});

const ResponseListCard = ({
  responses
}) => {
  const {
    container
  } = useStyles$7();
  return React__default.createElement("div", {
    className: container
  }, React__default.createElement(ScrollableBox$1, {
    scrollable: true,
    maxHeight: "100%",
    px: 4
  }, React__default.createElement(ResponseList, null, responses.map(({
    sentiment,
    content: text
  }) => React__default.createElement(ResponseListItem, {
    key: text,
    icon: sentiment && React__default.createElement(SentimentIcon, {
      sentiment: sentiment,
      title: sentiment,
      size: "md"
    })
  }, text)))));
};

const SentimentLegend = () => React__default.createElement(Box, {
  element: "ul",
  display: "flex",
  flexJustify: "center"
}, SENTIMENTS_ORDERED.map(sentiment => React__default.createElement(Box, {
  key: sentiment,
  display: "flex",
  element: "li",
  mx: 2
}, React__default.createElement(SentimentIcon, {
  sentiment: sentiment,
  size: "sm",
  title: "" // Labelled by sibling element
  ,
  "aria-labelledby": `${sentiment}-label`
}), React__default.createElement(Box, {
  ml: 2
}, React__default.createElement(Typography, {
  id: `${sentiment}-label`,
  component: "p",
  variant: "bodySm"
}, React__default.createElement(T, {
  desc: COPY$3.SENTIMENT,
  values: {
    sentiment
  }
}))))));

const useStyles$6 = createUseStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: [0, theme.spacing[3]]
  },
  header: {
    flex: 'none'
  },
  chartContainer: {
    // TODO:  maxWidth should be applied to the component itself
    // the component should also be responsive
    maxWidth: '1024px',
    width: '100%',
    margin: 'auto'
  },
  footer: {
    flex: 'none',
    borderTop: [theme.keyline.borderWidth, theme.keyline.borderStyle, theme.keyline.borderColor],
    padding: [theme.spacing[4], 0]
  }
}));
const COPY$2 = defineMessages({
  SENTIMENT_CHART_TITLE: {
    id: 'SentimentChartCard.SENTIMENT_CHART_TITLE',
    defaultMessage: 'How people feel: ',
    description: '[Type: header][Vis: med] - title of the chart'
  }
});

const SentimentChartCard = props => {
  const {
    container,
    header,
    footer,
    chartContainer
  } = useStyles$6();
  return React__default.createElement("div", {
    className: container
  }, React__default.createElement(Box, {
    className: header,
    mb: 4,
    pb: 1
  }, React__default.createElement(Typography, {
    variant: "body",
    align: "center"
  }, React__default.createElement(T, {
    desc: COPY$2.SENTIMENT_CHART_TITLE
  }))), React__default.createElement("div", {
    className: chartContainer
  }, React__default.createElement(SentimentBarChart, { ...props
  })), React__default.createElement("div", {
    className: footer
  }, React__default.createElement(SentimentLegend, null)));
};

const useStyles$5 = createUseStyles(theme => ({
  slideBoxContainer: {
    width: ({
      width = '100%'
    }) => width,
    height: ({
      height = '100%'
    }) => height,

    /** Artbitrary - Should have some sort of min/max to display content properly */

    /* minWidth: '300px',
    minHeight: '200px', */
    display: 'flex',
    flexDirection: 'column'
  },
  slideBoxViewport: {
    display: 'flex',
    flex: '1 1 auto',
    overflow: 'hidden',
    position: 'relative'
  },
  slideBoxList: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%',
    position: 'relative',
    height: '100%',
    transform: ({
      position = 0
    }) => `translateX(${-(position * 100)}%)`,
    transition: 'transform .3s ease-out'
  },
  slideBoxListItem: {
    display: 'flex',
    flex: '1 0 auto',
    width: '100%',
    padding: `0 ${theme.spacing[4]}px`
  },
  slideBoxNavContainer: {
    flex: 'none',
    display: 'flex',
    justifyContent: 'stretch',
    alignItems: 'center',
    padding: '15px',
    boxSizing: 'border-box'
  },
  slideBoxNavIndicator: {
    flexGrow: 1
  }
}));
/**
 * SlideBox Item Wrapper for layout
 */

const SlideBoxItem = ({
  children,
  position,
  index
}) => {
  const isHidden = position !== index;
  const tabIndex = isHidden ? -1 : undefined;
  const theme = useTheme();
  const {
    slideBoxListItem
  } = useStyles$5({
    theme,
    position
  });
  return React__default.createElement("li", {
    className: slideBoxListItem,
    "aria-hidden": isHidden,
    tabIndex: tabIndex
  }, children);
};

const COPY$1 = defineMessages({
  NEXT_BUTTON: {
    id: 'SlideBox.NEXT_BUTTON',
    defaultMessage: 'NEXT',
    description: '[Type: button][Vis: med] - next button of slider'
  },
  PREVIOUS_BUTTON: {
    id: 'SlideBox.PREVIOUS_BUTTON',
    defaultMessage: 'PREV',
    description: '[Type: button][Vis: med] - previous button of slider'
  },
  NEXT_SLIDE: {
    id: 'SlideBox.NEXT_SLIDE',
    defaultMessage: 'Next slide',
    description: '[Type: label][Vis: low] - next button label of slider'
  },
  PREVIOUS_SLIDE: {
    id: 'SlideBox.PREVIOUS_SLIDE',
    defaultMessage: 'Previous slide',
    description: '[Type: label][Vis: low] - previous button label of slider'
  },
  SLIDE_INDICATOR: {
    id: 'SlideBox.SLIDE_INDICATOR',
    defaultMessage: '{position, number} of {total, number}',
    description: '[Type: label][Vis: low] - slide pagination'
  }
});
/**
 * Navigation Control for the SlideBox
 */

const SlideBoxNavigation = ({
  onChange,
  position = 0,
  total = 0
}) => {
  const {
    slideBoxNavContainer,
    slideBoxNavIndicator
  } = useStyles$5({});
  return React__default.createElement("div", {
    className: slideBoxNavContainer
  }, React__default.createElement(Button, {
    size: "sm",
    buttonType: "button",
    variant: "text",
    label: t(COPY$1.PREVIOUS_SLIDE),
    onClick: () => onChange && onChange(position - 1),
    disabled: position === 0,
    color: "link"
  }, React__default.createElement(IconArrowLeft, {
    title: "left arrow"
  }), React__default.createElement(T, {
    desc: COPY$1.PREVIOUS_BUTTON
  })), React__default.createElement("div", {
    className: slideBoxNavIndicator
  }, React__default.createElement(Typography, {
    variant: "bodySm",
    color: "darkMuted",
    align: "center"
  }, React__default.createElement(T, {
    desc: COPY$1.SLIDE_INDICATOR,
    values: {
      position: position + 1,
      total: total + 1
    }
  }))), React__default.createElement(Button, {
    size: "sm",
    buttonType: "button",
    onClick: () => onChange && onChange(position + 1),
    disabled: position === total,
    color: "link",
    variant: "text",
    label: t(COPY$1.NEXT_SLIDE)
  }, React__default.createElement(T, {
    desc: COPY$1.NEXT_BUTTON
  }), React__default.createElement(IconArrowRight, {
    title: "right arrow",
    size: "md",
    color: "link"
  })));
};
/**
 * Carousel-like component
 */


const SlideBox = ({
  children,
  height,
  width,
  showNavBar,
  ...props
}) => {
  const [position, setPosition] = useState(0);
  const {
    slideBoxContainer,
    slideBoxViewport,
    slideBoxList
  } = useStyles$5({
    height,
    width,
    position
  });
  const totalItems = React__default.Children.count(children);
  const useNavBar = showNavBar !== undefined ? showNavBar : totalItems > 1;
  return React__default.createElement("div", {
    className: slideBoxContainer,
    ...props
  }, React__default.createElement("div", {
    className: slideBoxViewport,
    "aria-live": "polite"
  }, React__default.createElement("ul", {
    className: slideBoxList
  }, React__default.Children.map(children, (slide, index) => React__default.createElement(SlideBoxItem, {
    index: index,
    position: position
  }, slide)))), useNavBar && React__default.createElement(SlideBoxNavigation, {
    onChange: setPosition,
    position: position,
    total: totalItems - 1
  }));
};

const useStyles$4 = createUseStyles(theme => ({
  responseSlideCard: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%'
  },
  centeredBox: {
    /**
     * `justify-content: safe center` is not fully supported
     * @see https://stackoverflow.com/a/33455342 (Solution 1);
     * @see https://stackoverflow.com/a/33856609 (Auto Margins)
     */
    margin: 'auto'
  },
  blockQuote: {
    fontSize: ({
      fontSize
    }) => `${fontSize}%`,
    textAlign: 'center',
    wordBreak: 'break-word'
  },
  sentimentContainer: {
    display: 'flex',
    flex: 'none',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',

    /* NOTE: prod contains 10px for padding: use 8px or 12px? - current is 12px */
    padding: `${theme.spacing[3]}px`
  }
}));
/**
 * Slide to display text with optional Sentiment
 */

const SentimentSlideCard = ({
  response
}) => {
  const [slideCard, {
    width: slideWidth
  }] = useDimensions();
  const fontSize = React__default.useMemo(() => {
    const textCount = response.content.length;
    let font = 100;

    if (textCount <= 50) {
      font = 300;
    } else if (textCount <= 120) {
      font = 200;
    } else if (textCount <= 240) {
      font = 150;
    } // NOTE: Unknown calculation: 650 <-- what does this represent?


    return Math.max(100, font * slideWidth / 650);
  }, [response, slideWidth]);
  const {
    responseSlideCard,
    centeredBox,
    blockQuote,
    sentimentContainer
  } = useStyles$4({
    fontSize
  });
  return React__default.createElement("div", {
    ref: slideCard,
    className: responseSlideCard
  }, React__default.createElement(ScrollableBox$1, {
    scrollable: true,
    flex: true,
    tabIndex: 0
  }, React__default.createElement("div", {
    className: centeredBox,
    translate: "no"
  }, React__default.createElement(Typography, {
    variant: "body",
    align: "center"
  }, React__default.createElement("span", {
    className: blockQuote
  }, "\u201C", response.content, "\u201D")))), response.sentiment && response.sentiment !== 'unknown' && React__default.createElement("div", {
    className: sentimentContainer
  }, React__default.createElement(SentimentIcon, {
    sentiment: response.sentiment,
    title: "negative",
    size: "lg"
  }), React__default.createElement(Box, {
    ml: 2
  }, React__default.createElement(Typography, {
    variant: "bodySm",
    color: "darkMuted"
  }, React__default.createElement(T, {
    desc: COPY$3.SENTIMENT,
    values: {
      sentiment: response.sentiment
    }
  })))));
};
/**
 * OpenEnded Response SlideBox
 */


const ResponseSlider = ({
  responses,
  ...slideBoxProps
}) => {
  return React__default.createElement(SlideBox, { ...slideBoxProps
  }, responses.map(response => {
    return React__default.createElement(SentimentSlideCard, {
      key: `slidecard-${response.id}`,
      response: response
    });
  }));
};

const RenderDashboard = ({
  dashboardType,
  responses
}) => {
  if (!responses || responses.length === 0) {
    return React__default.createElement(ResponseError, null);
  }

  if (dashboardType === 'list') {
    return React__default.createElement(ResponseListCard, {
      responses: responses
    });
  }

  if (dashboardType === 'sentiment-chart') {
    return React__default.createElement(SentimentChartCard, { ...convertTextResponsesToSentiments(responses)
    });
  }

  return React__default.createElement(ResponseSlider, {
    responses: responses
  });
};

const OpenEndedDashboardItem = ({
  dashboardType,
  responses,
  ...layoutProps
}) => {
  return React__default.createElement(DashboardCardLayoutTemplate, { ...layoutProps
  }, React__default.createElement(RenderDashboard, {
    dashboardType: dashboardType,
    responses: responses
  }));
};

var OpenEndedDashboardItem$1 = withErrorBoundary(OpenEndedDashboardItem);
/* eslint-disable react/no-array-index-key */

const SpacerSquares = ({
  range,
  width,
  height,
  offset,
  spacing
}) => React__default.createElement(React__default.Fragment, null, range.map((r, i) => React__default.createElement(React__default.Fragment, {
  key: `spacer-rect-${i}`
}, React__default.createElement("rect", {
  fill: "white",
  height: height,
  width: spacing * 2,
  x: i * offset + width,
  y: 0,
  style: {
    zIndex: 999
  }
}))));

const RatingBarProgress = ({
  rating,
  fill,
  width,
  height,
  maxRating,
  spacing = 0
}) => {
  const animationRef = useRef();
  const prevRating = useRef(0);
  useSSRSafeLayoutEffect(() => {
    prevRating.current = rating;
    const animation = animationRef === null || animationRef === void 0 ? void 0 : animationRef.current;

    if (animation && animation.beginElement) {
      animation.beginElement();
    }
  }, [rating]); // Adjust for spacer-gaps; progress does not take this in to account normally.

  const spacerPercent = spacing * 2 / 100;
  return React__default.createElement("rect", {
    fill: fill,
    height: height - 0.2,
    width: width,
    x: 0,
    y: 0.1
  }, React__default.createElement("animate", {
    ref: r => {
      animationRef.current = r;
    },
    attributeName: "width",
    from: `${(prevRating.current / maxRating - spacerPercent) * 100}%`,
    to: `${(rating / maxRating - spacerPercent) * 100}%`,
    dur: "1s",
    fill: "freeze"
  }));
};
/* eslint-disable react/no-array-index-key */


const RatingIcons = ({
  range,
  width,
  offset,
  iconComponent: Icon
}) => React__default.createElement(React__default.Fragment, null, range.map((r, i) => React__default.createElement(React__default.Fragment, {
  key: `icon-rect-${i}`
}, React__default.createElement("g", null, React__default.createElement(Icon, {
  x: i * offset + width / 4,
  width: width / 2,
  title: "",
  color: "light"
})))));
/* eslint-disable react/no-array-index-key */


const EmptySquares = ({
  range,
  fill,
  width,
  height,
  offset
}) => React__default.createElement(React__default.Fragment, null, range.map((r, i) => React__default.createElement("rect", {
  key: `empty-rect-${i}`,
  fill: fill,
  height: height,
  width: width,
  x: i * offset,
  y: 0
}))); // svg size parameter to ensure scaling


const baseSize = 10; // spacing between block elements

const spacing = 0.5; // offset calculation for spacing between elements

const xOffset = Math.ceil((baseSize * 2 + spacing) / 2);

const RatingBar = ({
  maxRating = 5,
  rating,
  fillColor,
  emptyColor = '#D0D2D3',
  iconComponent
}) => {
  if (!Number.isFinite(rating)) {
    return null;
  } // dynamic box size calculation for svg scaling


  const boxSizeX = Math.ceil((baseSize + spacing) * maxRating + spacing * maxRating);
  const range = Array.from(new Array(maxRating));
  return React__default.createElement("svg", {
    viewBox: `0 0 ${boxSizeX} ${baseSize}`,
    preserveAspectRatio: "xMidYMid",
    width: "100%",
    height: "100%"
  }, React__default.createElement(EmptySquares, {
    range: range,
    width: baseSize,
    height: baseSize,
    fill: emptyColor,
    offset: xOffset
  }), React__default.createElement(RatingBarProgress, {
    fill: fillColor,
    height: baseSize,
    width: baseSize,
    rating: rating,
    maxRating: maxRating,
    spacing: spacing
  }), React__default.createElement(SpacerSquares, {
    range: range,
    width: baseSize,
    height: baseSize,
    offset: xOffset,
    spacing: spacing
  }), React__default.createElement(RatingIcons, {
    range: range,
    width: baseSize,
    offset: xOffset,
    iconComponent: iconComponent
  }));
};

const useStyles$3 = createUseStyles(theme => ({
  container: ({
    width = 0,
    height = 0
  }) => ({
    width: width || '100%',
    height: height || '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }),
  scaleContainer: {
    display: 'inline-block',
    transform: ({
      scaleAmount
    }) => `scale(${scaleAmount})`
  },
  content: {
    padding: theme.spacing[3]
  }
}));
const useStyles$2 = createUseStyles(theme => ({
  ratingWrapper: {
    maxWidth: '200px',
    display: 'flex',
    paddingTop: theme.spacing[3]
  }
}));

const RatingWrapper = ({
  children
}) => {
  const {
    ratingWrapper
  } = useStyles$2();
  return React__default.createElement("div", {
    className: ratingWrapper
  }, children);
};

const COPY = defineMessages({
  AVERAGE_RATING: {
    id: 'tablist.AVERAGE_RATING',
    defaultMessage: `average rating`,
    description: '[Type: label][Vis: high] - average rating title'
  }
});

function useScale(outer = {}, inner = {}, {
  min = 0,
  max = 10
}) {
  const [scale, setScale] = React__default.useState(1);
  React__default.useMemo(() => {
    if (outer.width && outer.height && inner.width && inner.height) {
      const size = Math.min(outer.height / inner.height, outer.width / inner.width);
      const result = Math.max(min, Math.min(size, max));
      setScale(result);
    }
  }, [inner.height, inner.width, outer.height, outer.width, min, max]);
  return scale;
}
/** Rating Chart */


const RatingChart = ({
  rating,
  fillColor = '#3FC688',
  maxRating = 5,
  iconComponent: Icon,
  width,
  height,
  scale = {
    max: 1.3
  }
}) => {
  const [containerRef, outerDimensions] = useDimensions();
  const [contentRef, innerDimensions] = useDimensions();
  const scaleAmount = useScale(outerDimensions, innerDimensions, scale);
  const {
    container,
    scaleContainer,
    content
  } = useStyles$3({
    width,
    height,
    scaleAmount
  });

  if (!Number.isFinite(rating)) {
    return null;
  }

  const normalizedRating = Math.min(Math.max(rating, 0), maxRating);
  return React__default.createElement("div", {
    ref: containerRef,
    className: container
  }, React__default.createElement("div", {
    className: scaleContainer
  }, React__default.createElement("div", {
    ref: contentRef,
    className: content
  }, React__default.createElement(Typography, {
    variant: "hero1",
    align: "center",
    key: "hero-title"
  }, normalizedRating.toFixed(1), React__default.createElement("span", {
    style: {
      color: fillColor
    }
  }, React__default.createElement(Icon, {
    title: "",
    size: "lg"
  }))), React__default.createElement(Typography, {
    variant: "sectionTitle",
    align: "center",
    key: "hero-subtitle"
  }, React__default.createElement(T, {
    desc: COPY.AVERAGE_RATING
  })), React__default.createElement(Box, {
    display: "flex",
    flexJustify: "center",
    flexAlign: "center",
    key: "rating-bar"
  }, React__default.createElement(RatingWrapper, {
    key: "rating-wrapper"
  }, React__default.createElement(RatingBar, {
    key: "rating-bar",
    maxRating: maxRating,
    rating: normalizedRating,
    fillColor: fillColor,
    iconComponent: Icon
  }))))));
};

defineMessages({
  ANSWERED: {
    id: 'ratingchart.ANSWERED',
    defaultMessage: `Answered: {number}`,
    description: '[Type: label][Vis: high] - amount of responses answered'
  },
  SKIPPED: {
    id: 'ratingchart.SKIPPED',
    defaultMessage: `Skipped: {skipped}`,
    description: '[Type: label][Vis: high] - amount of responses skippeed'
  },
  TOOLTIP: {
    id: 'ratingchart.TOOLTIP',
    defaultMessage: `{label} ({percent, number, percent})`,
    description: '[Type: label][Vis: high] - percentage tooltip'
  },
  RATING: {
    id: 'progress.RATING',
    defaultMessage: `{rating} star`,
    description: '[Type: label][Vis: high] - rating amount'
  }
}); // Fixes 'Rating Star Labels' (i.e. 1 Star, 5 Star, etc) wrapping on two lines when viewport is
// smaller than 430px.
// 2.666666666666667em = 40px
// Resolves: #ANEXP-1338, #RAWR-559

createUseStyles({
  ratingLabelColumnStyles: {
    minWidth: '2.666666666666667em'
  },
  ratingProgressLine: {
    maxWidth: '100%'
  }
});
/** checks for `{name: string, data: any}[]` */

const isTwoDimensionalChartData = a => Array.isArray(a) && a.every(i => typeof i === 'object' && 'name' in i && 'data' in i && i.data.every(d => typeof d === 'number')); // Chart Type Guards


const isBarChartData = a => {
  return Array.isArray(a) && a.every(i => typeof i === 'number') || isTwoDimensionalChartData(a);
};

const isStackedBarChartData = a => isTwoDimensionalChartData(a);

const isLineChartData = a => isTwoDimensionalChartData(a);

const isAreaChartData = a => isTwoDimensionalChartData(a);

const isPieOrDonutChartData = a => {
  return Array.isArray(a) && a.every(i => 'name' in i && 'value' in i && 'numberOfResponses' in i);
};

const splitLabel = str => {
  if (typeof str === 'string' && str.length < 13) {
    return 13;
  }

  for (let i = 13; i >= 0; i -= 1) {
    if (typeof str === 'string' && str.charAt(i) === ' ') {
      return i;
    }
  }

  return 11;
};

const formatLabelVerticalAxis = ({
  value
}) => {
  const newValue = value.toString();
  const splitPoint = splitLabel(newValue);
  const strTop = newValue.slice(0, splitPoint);
  const strBottom = newValue.slice(splitPoint, newValue.length);
  return `<div aria-label='${value}' title='${value}' align="center" style="width:60px;">
<div style="text-align: right">
${strTop}
</div>
<div style="overflow:hidden; text-overflow:ellipsis; max-height:14px; text-align:end; width:60px">
${strBottom}
</div>
</div>`;
};

function formatLabelHorizontalAxis(label) {
  const labelClipped = label.substring(0, 80);
  return label.length > 76 ? `${labelClipped}...` : label;
}

const defaultChartColors = ['#00BF6F', '#507CB6', '#F9BE00', '#6BC8CD', '#FF8B4F', '#7D5E90', '#D25F90', '#C7B879', '#DB4D5C', '#768086'];

const themedOptions = theme => ({
  colors: defaultChartColors,
  chart: {
    spacingRight: 0,
    style: {
      fontFamily: theme.type.fontFamily.base
    },
    plotBackgroundColor: theme.palette.background.light
  },
  title: {
    style: {
      fontFamily: theme.type.fontFamily.base,
      fontWeight: theme.type.fontWeight.medium.toString(),
      fontSize: theme.type.fontSize.cardTitle.toString(),
      color: theme.palette.text.dark
    },
    text: '' // remove the chart title by default

  },
  xAxis: {
    title: {
      text: null // use "null" to hide x-axis title

    },
    labels: {
      style: {
        fontFamily: theme.type.fontFamily.base,
        fontWeight: theme.type.fontWeight.regular.toString(),
        color: theme.palette.text.dark,
        maxWidth: 10
      },
      overflow: 'justify',
      useHTML: true,

      formatter() {
        const label = this.axis.defaultLabelFormatter.call(this); // TODO: revisit this after dropping IE11 support

        return formatLabelHorizontalAxis(label);
      }

    },
    lineColor: theme.palette.border.main,
    tickLength: 0
  },
  yAxis: {
    // Do not change to an array.  Keep as single object.  See BarChart.tsx type assertion for YAxisOptions.
    labels: {
      overflow: 'justify',
      style: {
        fontFamily: theme.type.fontFamily.base,
        paddingTop: theme.spacing[3],
        fontWeight: theme.type.fontWeight.regular.toString(),
        color: theme.palette.text.dark
      },
      useHTML: true
    },
    title: {
      text: null
    },
    offset: 0,
    lineColor: theme.palette.border.main,
    lineWidth: 1,
    gridLineColor: theme.palette.border.muted
  },
  plotOptions: {
    series: {
      animation: false,
      enableMouseTracking: false,
      dataLabels: {
        style: {
          fontWeight: theme.type.fontWeight.medium.toString(),
          fontSize: theme.type.fontSize.bodySm.toString(),
          color: theme.palette.text.dark,
          textShadow: `-1px -1px 0 ${theme.palette.text.light}, 1px -1px 0 ${theme.palette.text.light}, -1px 1px 0 ${theme.palette.text.light}, 1px 1px 0 ${theme.palette.text.light}`
        },
        useHTML: true
      },
      point: {
        events: {
          legendItemClick() {
            return false;
          }

        }
      },
      events: {
        legendItemClick() {
          return false; // disables legend item toggling
        }

      },
      states: {
        hover: {
          enabled: false
        },
        inactive: {
          enabled: false
        }
      }
    }
  },
  legend: {
    labelFormatter() {
      return `<div style="
      overflow: hidden;
      text-overflow: ellipsis;
      ">${this.name}</div>`;
    },

    enabled: false,
    floating: false,
    borderWidth: 0,
    symbolRadius: 2,
    x: 0,
    y: 5,
    useHTML: true,
    itemDistance: 20,
    itemStyle: {
      color: theme.palette.text.dark,
      fontFamily: theme.type.fontFamily.base,
      fontSize: theme.type.fontSize.bodySm.toString(),
      fontWeight: theme.type.fontWeight.light.toString(),
      lineHeight: '20px'
    },
    itemHoverStyle: {
      color: theme.palette.text.dark
    }
  },
  tooltip: {
    animation: false,
    hideDelay: 100,
    backgroundColor: theme.palette.background.dark,
    borderWidth: 0,
    shadow: false,
    headerFormat: '',
    followPointer: true,
    style: {
      fontFamily: theme.type.fontFamily.base,
      fontSize: theme.type.fontSize.bodySm.toString(),
      color: theme.palette.text.dark
    },
    useHTML: true
  },
  credits: {
    enabled: false
  },
  accessibility: {
    enabled: true
  }
});

const useDefaultOptions = () => {
  const theme = useTheme();
  return themedOptions(theme);
};

var _a, _b;

HighchartsAccessibility(Highcharts); // support additional HTML/SVG attributes

(_a = Highcharts.AST) === null || _a === void 0 ? void 0 : _a.allowedAttributes.push('align');
(_b = Highcharts.AST) === null || _b === void 0 ? void 0 : _b.allowedAttributes.push('title');
const useStyles$1 = createUseStyles({
  container: {
    '& .highcharts-container': {
      direction: 'inherit'
    }
  }
});
/** Wrapper around `HighchartsReact` with default values - base of all other charts */

const BaseChart = ({
  options
}) => {
  const base = useDefaultOptions();
  const {
    container
  } = useStyles$1();
  return React__default.createElement(HighchartsReact, {
    containerProps: {
      style: {
        width: '100%',
        height: '100%'
      },
      className: container
    },
    highcharts: Highcharts,
    options: merge(base, options)
  });
};

const getBarChartOptions = (data, options, isPercentage = false, decimalPlaces = 0) => {
  return merge( // overwritable defaults
  {
    plotOptions: {
      bar: {
        colorByPoint: true,
        dataLabels: {
          format: `{y:.${decimalPlaces}f}${isPercentage ? '%' : ''}`,
          inside: false
        }
      }
    },
    xAxis: {
      labels: {
        formatter: formatLabelVerticalAxis
      }
    },
    yAxis: {
      labels: {
        format: `{value}${isPercentage ? '%' : ''}`
      }
    }
  }, options, // not overwritable
  {
    series: isTwoDimensionalChartData(data) ? data.map(d => ({
      name: d.name,
      data: d.data,
      type: 'bar'
    })) : [{
      data,
      type: 'bar'
    }]
  });
};

const BarChart = ({
  data,
  options,
  isPercentage,
  decimalPlaces
}) => {
  const barChartOptions = getBarChartOptions(data, options, isPercentage, decimalPlaces);
  return React__default.createElement(BaseChart, {
    options: barChartOptions
  });
};

const getColumnChartOptions = (data, options, isPercentage = false, decimalPlaces = 0) => {
  return merge( // overwritable defaults
  {
    plotOptions: {
      column: {
        colorByPoint: true,
        dataLabels: {
          format: `{y:.${decimalPlaces}f}${isPercentage ? '%' : ''}`,
          inside: false
        }
      }
    },
    yAxis: {
      labels: {
        format: `{value}${isPercentage ? '%' : ''}`
      }
    }
  }, options, // not overwritable
  {
    series: isTwoDimensionalChartData(data) ? data.map(d => ({
      name: d.name,
      data: d.data,
      type: 'column'
    })) : [{
      data,
      type: 'column'
    }]
  });
};

const ColumnChart = ({
  data,
  options,
  isPercentage,
  decimalPlaces
}) => {
  const columnChartOptions = getColumnChartOptions(data, options, isPercentage, decimalPlaces);
  return React__default.createElement(BaseChart, {
    options: columnChartOptions
  });
};

function getPieChartOptions(data, options) {
  const pieChartData = {
    data: data.map(({
      name,
      value,
      numberOfResponses
    }) => ({
      name,
      y: value,
      numberOfResponses
    })),
    type: 'pie'
  };
  return merge(options, // not overwritable
  {
    series: [pieChartData],
    chart: {
      type: 'pie'
    }
  });
}

const PieChart = ({
  data,
  options
}) => {
  const pieChartOptions = getPieChartOptions(data, options);
  return React__default.createElement(BaseChart, {
    options: pieChartOptions
  });
};
/** Build Donut specific options - rest is done in PieChart */


const getDonutChartOptions = options => merge( // overwritable defaults
{
  plotOptions: {
    pie: {
      innerSize: '40%'
    }
  }
}, options);
/** Donut chart is a special type of PieChart (and uses it internally) */


const DonutChart = ({
  data,
  options
}) => {
  const donutChartOptions = getDonutChartOptions(options);
  return React__default.createElement(PieChart, {
    data: data,
    options: donutChartOptions
  });
};

const getStackedBarChartOptions = (data, options, isPercentage = false, showLabels = false, decimalPlaces = 0) => {
  const series = data.map(stack => ({ ...stack,
    type: 'bar'
  }));
  return merge( // overwritable defaults
  {
    legend: {
      enabled: true,
      reversed: true // reverse series labels have have same order as `data` (Highcharts flips them) ref: https://www.highcharts.com/demo/bar-stacked

    },
    plotOptions: {
      bar: {
        stacking: isPercentage ? 'percentage' : 'normal',
        dataLabels: {
          format: `{y:.${decimalPlaces}f}${isPercentage ? '%' : ''}`,
          style: {
            textOutline: '1px contrast'
          },
          useHTML: false // needed for textOutline

        },
        groupPadding: 0,
        maxPointWidth: 100 // TODO: refine this

      }
    },
    xAxis: {
      labels: {
        enabled: showLabels
      }
    },
    yAxis: {
      labels: {
        format: `{value}${isPercentage ? '%' : ''}`
      }
    }
  }, options, // not overwritable
  {
    series,
    chart: {
      type: 'bar'
    }
  });
};

const StackedBarChart = ({
  data,
  options,
  isPercentage,
  showLabels,
  decimalPlaces
}) => {
  const barChartOptions = getStackedBarChartOptions(data.slice().reverse(), // reverse series data have have same order as data inserted (Highcharts flips them)
  options, isPercentage, showLabels, decimalPlaces);
  return React__default.createElement(BaseChart, {
    options: barChartOptions
  });
};

const getStackedColumnChartOptions = (data, options, isPercentage = false, showLabels = false, decimalPlaces = 0) => {
  const series = data.map(stack => ({ ...stack,
    type: 'column'
  }));
  return merge( // overwritable defaults
  {
    legend: {
      enabled: true,
      reversed: true // reverse series labels have have same order as `data` (Highcharts flips them) ref: https://www.highcharts.com/demo/bar-stacked

    },
    plotOptions: {
      column: {
        stacking: isPercentage ? 'percentage' : 'normal',
        dataLabels: {
          format: `{y:.${decimalPlaces}f}${isPercentage ? '%' : ''}`,
          style: {
            textOutline: '1px contrast'
          },
          useHTML: false // needed for textOutline

        },
        groupPadding: 0,
        maxPointWidth: 100 // TODO: refine this

      }
    },
    xAxis: {
      labels: {
        enabled: showLabels
      }
    },
    yAxis: {
      labels: {
        format: `{value}${isPercentage ? '%' : ''}`
      }
    }
  }, options, // not overwritable
  {
    series,
    chart: {
      type: 'column'
    }
  });
};

const StackedColumnChart = ({
  data,
  options,
  isPercentage,
  showLabels,
  decimalPlaces
}) => {
  const columnChartOptions = getStackedColumnChartOptions(data.slice().reverse(), // reverse series data have have same order as data inserted (Highcharts flips them)
  options, isPercentage, showLabels, decimalPlaces);
  return React__default.createElement(BaseChart, {
    options: columnChartOptions
  });
};

const getLineChartOptions = (data, options, isPercentage = false, decimalPlaces = 0) => {
  const series = data.map(el => ({ ...el,
    type: 'line'
  }));
  return merge( // overwritable defaults
  {
    plotOptions: {
      line: {
        dataLabels: {
          enabled: true,
          format: `{y:.${decimalPlaces}f}${isPercentage ? '%' : ''}`,
          style: {
            textOutline: '1px contrast'
          },
          useHTML: false // needed for textOutline

        }
      }
    },
    tooltip: {
      // TODO: add show tooltip
      enabled: false
    },
    yAxis: {
      labels: {
        format: `{value}${isPercentage ? '%' : ''}`
      }
    }
  }, options, // not overwritable
  {
    series,
    chart: {
      type: 'line'
    }
  });
};

const LineChart = ({
  data,
  options,
  isPercentage,
  decimalPlaces
}) => {
  const lineChartOptions = getLineChartOptions(data, options, isPercentage, decimalPlaces);
  return React__default.createElement(BaseChart, {
    options: lineChartOptions
  });
};

const getAreaChartOptions = (data, options, isPercentage = false, isStackable = false, decimalPlaces = 0) => {
  const series = data.map(el => ({ ...el,
    type: 'area'
  }));
  return merge( // overwritable defaults
  {
    plotOptions: {
      area: {
        dataLabels: {
          enabled: true,
          format: `{y:.${decimalPlaces}f}${isPercentage ? '%' : ''}`,
          style: {
            textOutline: '1px contrast'
          },
          useHTML: false // needed for textOutline

        },
        stacking: isStackable && isPercentage ? 'percent' : 'normal'
      }
    },
    tooltip: {
      // TODO: add show tooltip
      enabled: false
    },
    yAxis: {
      labels: {
        format: isPercentage ? '{value}%' : '{value}'
      },
      reversedStacks: false
    }
  }, options, // not overwritable
  {
    series,
    chart: {
      type: 'area'
    }
  });
};

const AreaChart = ({
  data,
  options,
  isPercentage,
  isStackable,
  decimalPlaces
}) => {
  const areaChartOptions = getAreaChartOptions(data, options, isPercentage, isStackable, decimalPlaces);
  return React__default.createElement(BaseChart, {
    options: areaChartOptions
  });
};

function getPieDataLabelFormatted(point, showInLegend, isDataInChart, decimalPlaces) {
  const {
    name,
    y,
    numberOfResponses
  } = point;

  if (!y) {
    // eslint-disable-next-line no-console
    console.error('"y" property for data point is undefined. The label cannot be formatted.');
    return '';
  }

  const nameLabel = name.length > 68 ? `${name.substring(0, 65)}...` : name;
  const dataLabel = isDataInChart ? `<br> ${numberFormat(y, decimalPlaces)}% (${numberOfResponses})` : '';
  return showInLegend ? dataLabel : nameLabel + dataLabel;
}

const getPieDonutChartMapping = (customization, data) => {
  const isDataInChart = customization.dataInChart;
  const showInLegend = customization.dataFormatLabel === 'Legend';
  const options = {
    colors: [...customization.colors],
    plotOptions: {
      pie: {
        cursor: 'default',
        showInLegend,
        dataLabels: {
          style: {
            width: 108 // width used in prod

          },
          enabled: showInLegend && isDataInChart || !showInLegend,

          formatter() {
            return getPieDataLabelFormatted(this.point, showInLegend, isDataInChart, customization.decimalPlaces);
          }

        }
      }
    },
    tooltip: {
      enabled: false
    },
    legend: {
      enabled: showInLegend,

      labelFormatter() {
        return this.name.length > 93 ? `${this.name.substring(0, 90)}...` : this.name;
      }

    }
  };
  return {
    options,
    data
  };
};
/**
 * Map the base options configuration for
 * bar, column, stacked bar and stacked column charts.
 * This data may be augmented at the Chart level.
 */


const getCommonBarChartMapping = customization => {
  const {
    labels,
    colors,
    min,
    max,
    dataInChart
  } = customization;
  const showTooltip = !dataInChart;
  return {
    colors,
    yAxis: {
      min,
      max
    },
    xAxis: {
      categories: labels
    },
    tooltip: {
      enabled: showTooltip
    }
  };
};

const getBarChartMapping = (customization, data) => {
  const isPercentage = customization.axisScale === 'Percentage';
  const isDataMultiDimension = isTwoDimensionalChartData(data);
  const commonBarChartOptions = getCommonBarChartMapping(customization);
  const options = merge(commonBarChartOptions, {
    legend: {
      enabled: isDataMultiDimension
    },
    plotOptions: {
      bar: {
        dataLabels: {
          enabled: customization.dataInChart
        },
        colorByPoint: !isDataMultiDimension
      }
    }
  });
  return {
    data,
    options,
    isPercentage,
    decimalPlaces: customization.decimalPlaces
  };
};

const getColumnChartMapping = (customization, data) => {
  const isPercentage = customization.axisScale === 'Percentage';
  const isDataMultiDimension = isTwoDimensionalChartData(data);
  const commonBarChartOptions = getCommonBarChartMapping(customization);
  const options = merge(commonBarChartOptions, {
    legend: {
      enabled: isDataMultiDimension
    },
    plotOptions: {
      column: {
        dataLabels: {
          enabled: customization.dataInChart
        },
        colorByPoint: !isDataMultiDimension
      }
    }
  });
  return {
    data,
    options,
    isPercentage,
    decimalPlaces: customization.decimalPlaces
  };
};

const getStackedBarChartMapping = (customization, data) => {
  const isPercentage = customization.axisScale === 'Percentage';
  const commonBarChartOptions = getCommonBarChartMapping(customization);
  const showLabels = data[0].data.length > 1;
  const options = merge(commonBarChartOptions, {
    plotOptions: {
      bar: {
        dataLabels: {
          enabled: customization.dataInChart
        },
        colorByPoint: false
      }
    }
  });
  return {
    data,
    options,
    isPercentage,
    showLabels,
    decimalPlaces: customization.decimalPlaces
  };
};

const getStackedColumnChartMapping = (customization, data) => {
  const isPercentage = customization.axisScale === 'Percentage';
  const commonBarChartOptions = getCommonBarChartMapping(customization);
  const showLabels = data[0].data.length > 1;
  const options = merge(commonBarChartOptions, {
    plotOptions: {
      column: {
        dataLabels: {
          enabled: customization.dataInChart
        },
        colorByPoint: false
      }
    }
  });
  return {
    data,
    options,
    isPercentage,
    showLabels,
    decimalPlaces: customization.decimalPlaces
  };
};

const getLineAreaChartMapping = (customization, data) => {
  const {
    colors,
    labels,
    max,
    min
  } = customization;

  const isTwoDimensionalLineAreaData = chartData => chartData[0].name !== '';

  return {
    colors,
    xAxis: {
      categories: labels
    },
    yAxis: {
      min,
      max
    },
    legend: {
      enabled: isTwoDimensionalLineAreaData(data)
    }
  };
};

const getLineChartMapping = (customization, data) => {
  const isPercentage = customization.axisScale === 'Percentage';
  const commonLineChartOptions = getLineAreaChartMapping(customization, data);
  const options = merge(commonLineChartOptions, {
    plotOptions: {
      line: {
        dataLabels: {
          enabled: customization.dataInChart
        }
      }
    }
  });
  return {
    data,
    options,
    isPercentage,
    decimalPlaces: customization.decimalPlaces
  };
};

const getAreaChartMapping = (customization, data) => {
  const isPercentage = customization.axisScale === 'Percentage';
  const isStackable = data.length > 1;
  const commonLineChartOptions = getLineAreaChartMapping(customization, data);
  const options = merge(commonLineChartOptions, {
    plotOptions: {
      area: {
        dataLabels: {
          enabled: customization.dataInChart
        }
      }
    }
  });
  return {
    data,
    options,
    isPercentage,
    isStackable,
    decimalPlaces: customization.decimalPlaces
  };
};

const AggregateResponseChartSelector = ({
  data,
  customization
}) => {
  switch (customization.chartType) {
    case 'hbar':
      {
        assert(isBarChartData(data));
        return React__default.createElement(BarChart, { ...getBarChartMapping(customization, data)
        });
      }

    case 'vbar':
      {
        assert(isBarChartData(data));
        return React__default.createElement(ColumnChart, { ...getColumnChartMapping(customization, data)
        });
      }

    case 'donut':
      {
        assert(isPieOrDonutChartData(data));
        return React__default.createElement(DonutChart, { ...getPieDonutChartMapping(customization, data)
        });
      }

    case 'pie':
      {
        assert(isPieOrDonutChartData(data));
        return React__default.createElement(PieChart, { ...getPieDonutChartMapping(customization, data)
        });
      }

    case 'stacked_hbar':
      {
        assert(isStackedBarChartData(data));
        return React__default.createElement(StackedBarChart, { ...getStackedBarChartMapping(customization, data)
        });
      }

    case 'stacked_vbar':
      {
        assert(isStackedBarChartData(data));
        return React__default.createElement(StackedColumnChart, { ...getStackedColumnChartMapping(customization, data)
        });
      }

    case 'line':
      {
        assert(isLineChartData(data));
        return React__default.createElement(LineChart, { ...getLineChartMapping(customization, data)
        });
      }

    case 'area':
      {
        assert(isAreaChartData(data));
        return React__default.createElement(AreaChart, { ...getAreaChartMapping(customization, data)
        });
      }

    default:
      {
        return null;
      }
  }
};

const useStyles = createUseStyles(theme => ({
  /** for some reason, @wds/box doesn't play nice */
  box: {
    width: '100%',
    height: '100%',
    padding: [0, theme.spacing[3]]
  }
}));

const ChartDashboardItem = ({
  data,
  customization,
  ...props
}) => {
  const {
    box
  } = useStyles();
  const hasData = data && data.length > 0;
  return React__default.createElement(DashboardCardLayoutTemplate, { ...props
  }, React__default.createElement("div", {
    className: box
  }, hasData ? React__default.createElement(AggregateResponseChartSelector, { ...{
      data,
      customization
    }
  }) : React__default.createElement(ResponseError, null)));
};

var ChartDashboardItem$1 = withErrorBoundary(ChartDashboardItem);

const isRatingChart = props => {
  return typeof props.data === 'number';
};

const StarRating = ({ ...props
}) => {
  if (!isRatingChart(props)) {
    return React__default.createElement(ChartDashboardItem$1, { ...props
    });
  }

  const {
    data,
    customization,
    ...layoutProps
  } = props;
  return React__default.createElement(DashboardCardLayoutTemplate, { ...layoutProps
  }, React__default.createElement(RatingChart, {
    rating: data,
    ...customization
  }));
};

var StarRating$1 = withErrorBoundary(StarRating);
export { ChartDashboardItem$1 as ChartDashboardItem, OpenEndedDashboardItem$1 as CommentBox, defaultChartColors as DEFAULT_COLORS, OpenEndedDashboardItem$1 as OpenEndedDashboardItem, OpenEndedDashboardItem$1 as SingleTextbox, StarRating$1 as StarRating };